// c:\dev\adenin-artifacts\src\editor\process-llm-response.js
import { editorState } from './editor-state';
import { snapshot } from 'valtio';

function removeCodeBlockMarkers(codeString) {
  // Check if the string starts with ```jsx or similar and ends with ```
  if (typeof codeString === 'string' && codeString.startsWith('```') && codeString.endsWith('```')) {
    // Remove the first line if it contains ```jsx
    const lines = codeString.split('\n');
    if (lines[0].startsWith('```')) {
      lines.shift();
    }

    // Remove the last line if it's just ```
    if (lines.length > 0 && lines[lines.length - 1].trim() === '```') {
      lines.pop();
    }

    return lines.join('\n');
  }

  // If not formatted as expected, return the original string
  return codeString;
}

export function extractTag(message, tagName) {
  //console.log("Extracting tag:", tagName, "from message:xxx", message,"xxx");
  const regex = new RegExp(`<${tagName}\\b(?:\\s+[^>]*)?>\\s*([\\s\\S]*?)\\s*<\\/${tagName}\\b>`);
  const match = message.match(regex);
  if (match && match[1]) {
    const extractedText = match[1].trim();
    return {
      extractedText,
      remainingMessage: message.replace(regex, '')
    };
  }
  return {
    extractedText: null,
    remainingMessage: message ? message : ""
  };
}

function fixCode(code) {
  // llm makes common errors, fixing them
  let newCode = code.replace("import { ApiAccess } from '@/components/apiAccess';", "import ApiAccess from '@/components/apiAccess';");
  newCode = removeCodeBlockMarkers(newCode);
  return newCode;
}


function extractArtifact(content) {
  const artifactRegex = /<atArtifact(?:\s+[^>]*)?>[\s\S]*?<\/atArtifact>/;
  const attributeRegex = /(\w+)="([^"]*)"/g;

  const artifactMatch = content.match(artifactRegex);

  if (!artifactMatch) {
    return {
      type: "unknown",
      identifier: "",
      title: "",
      code: "",
    };
  }

  const openingTag = artifactMatch[0].match(/<atArtifact(?:\s+[^>]*)?>/)?.[0] || "";
  const attributes = {};
  let match;
  while ((match = attributeRegex.exec(openingTag)) !== null) {
    attributes[match[1]] = match[2];
  }

  const innerContent = artifactMatch[0]
    .replace(/<atArtifact(?:\s+[^>]*)?>/, "")
    .replace(/<\/atArtifact>/, "")
    .trim();

  return {
    type: attributes.type || "unknown",
    identifier: attributes.identifier || "",
    title: attributes.title || "",
    code: innerContent || "",
  };
}

export const processLLMResponseAndUpdateState = (result, setFileName) => {
  var response = {};
  console.log("LLM raw result content:", result);

  editorState.pushHistory(); // save current version before updating

  const artifact = extractArtifact(result);
  var { extractedText, remainingMessage } = extractTag(result, 'atSpecification');
  response.specification = extractedText;

  if (extractedText) {
    editorState.updateSpecification(extractedText);
  }

  result = remainingMessage;

  ({ extractedText, remainingMessage } = extractTag(result, 'atSpecificationUpdateReasoning'));
  response.specificationUpdateReasoning = extractedText || '';
  result = remainingMessage;

  ({ extractedText, remainingMessage } = extractTag(result, 'atArtifact'));
  response.result = remainingMessage?.trim() ?? '';

  const newFileName = artifact.identifier ? `${artifact.identifier}.jsx` : 'output.jsx';
  setFileName(newFileName);

  editorState.component.jsx = fixCode(artifact.code);

  response.version = editorState.pushHistory(); // get version of current updated state

  return response;
};

export const getMessages = async (input, images = []) => {
  const snap = snapshot(editorState);
  const systemPromptText = snap.prompts.system;

  const messages = [{ role: 'system', content: systemPromptText }];

  // Generate prompt based on whether specification exists
  let prompt;
  if (!snap.component.specification) {
    // Initial specification case - use input directly as prompt
    editorState.updateSpecification(input);
    input = '';
  }

  let sampleData = await editorState.getSampleData();
  prompt = generatePrompt(input, images.length > 0, sampleData);

  // Create user message with prompt and attach images if present
  const userMessage = createUserMessageWithImages(prompt, images);
  messages.push(userMessage);

  return messages;
};

function generatePrompt(input, hasImages, sampleData) {
  let prompt = '';

  // Add artifact section only if jsx code exists
  if (editorState.component.jsx) {
    prompt += `
<atArtifact>
${editorState.component.jsx}
</atArtifact>

The above code is based on the following specification:`;
  } else {
    prompt += `
Generate a JSX component with the following specification:`;
  }

  prompt += `
<atSpecification>
${editorState.component.specification}
</atSpecification>

${input ? `Now the following change should be applied${editorState.component.jsx ? ' to the code above' : ''}:
<atEdit>
${input}
</atEdit>

` : ''}${editorState.component.jsx ?
      'It is very important that only the requested change is applied to the code above and nothing else.\nAlways answer with the COMPLETE code in atArtifact. Do not omit any unchanged parts of the code.' :
      'Please generate the complete code based on the specification and the requested changes.'
    }

Your response must include:
1. The complete ${editorState.component.jsx ? 'updated ' : ''}code in <atArtifact> tags
2. <atSpecification> tag contains the component specification, including functional requirements (props, API endpoint, data source), visual requirements (styling, behavior, layout), 
and interaction requirements (click behavior, no preview pane)
2. Your reasoning about the specification update in <atSpecificationUpdateReasoning> tags, be sure to consider function, visual or interaction requirement changes or refinements!
3. If needed, an updated specification in <atSpecification> tags. Do not repeat the original specification when unchanged, only include atSpecification if it has been updated.
4. Only include user provided information in the <atSpecification>. Do not repeat any information or restrictions from the system prompt.
${hasImages ? `5. the information from the attached images should also be considered when updating <atSpecification>` : ''}

When analyzing whether to update the specification, consider if the edit contains any new information not in the original specification. 
The specification should include all relevant information provided by the user, like core functionality, functional requirements, data sources, parameters, 
but also formatting, presentation information, and any other details that are important for the code generation.
In your <atSpecificationUpdateReasoning> tag, explain in detail why you did or did not update the specification.
`;


  if (sampleData && sampleData !== "{}") {
    prompt += `
Use the following sample data for the API response:
${sampleData}
`;
  }

  console.log("LLM prompt:", prompt);
  return prompt;
}

function createUserMessageWithImages(prompt, images) {
  const userMessage = { role: 'user', content: prompt };

  // Add images if present (for vision models)
  if (images && images.length > 0) {
    userMessage.content = [
      { type: 'text', text: prompt },
      ...images.map(img => ({
        type: 'image_url',
        image_url: { url: img.src }
      }))
    ];
  }

  return userMessage;
}
